import { useEffect, useRef, useState } from 'react';
import type { DocumentSnapshot, FirestoreError } from 'firebase/firestore';

import { FIRESTORE_ENABLED } from '@/config/env';
import { db } from '@/lib/firebase';
import { reportStreamError } from './useFirestoreStream';

export interface DocumentState<T> {
  data: T | null;
  loading: boolean;
  error: Error | null;
}

export interface DocumentOptions<T> {
  path: string;
  parse: (raw: Record<string, unknown>) => T | null;
  enabled?: boolean;
  endsSessionOnDenial?: boolean;
}

/** Subscribes to one document, loading the 620 kB SDK on demand. */
async function subscribe(
  path: string,
  onData: (snapshot: DocumentSnapshot) => void,
  onError: (error: FirestoreError) => void,
): Promise<() => void> {
  const { doc, onSnapshot } = await import('firebase/firestore');
  return onSnapshot(doc(await db(), path), onData, onError);
}

export function useFirestoreDocument<T>({
  path,
  parse,
  enabled = true,
  endsSessionOnDenial = true,
}: DocumentOptions<T>): DocumentState<T> {
  const active = enabled && FIRESTORE_ENABLED;

  const [state, setState] = useState<DocumentState<T>>({
    data: null,
    loading: active,
    error: null,
  });

  // The latest parser; a new function identity does not reopen the document.
  const parseRef = useRef(parse);
  parseRef.current = parse;

  useEffect(() => {
    if (!active) {
      setState({ data: null, loading: false, error: null });
      return;
    }

    setState((previous) => ({ ...previous, loading: true, error: null }));

    let cancelled = false;
    let unsubscribe: (() => void) | undefined;

    subscribe(
      path,
      (snapshot) =>
        setState({
          data: snapshot.exists() ? parseRef.current(snapshot.data() as Record<string, unknown>) : null,
          loading: false,
          error: null,
        }),
      (error) => {
        if (endsSessionOnDenial) reportStreamError(error);
        setState({ data: null, loading: false, error });
      },
    )
      // The SDK loads asynchronously
      .then((stop) => (cancelled ? stop() : void (unsubscribe = stop)))
      .catch((error: Error) => setState({ data: null, loading: false, error }));

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, [active, path, endsSessionOnDenial]);

  return state;
}
